'use client';

import React, { type ReactNode, useCallback, useState } from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

import { useVisibilityInterval } from '@/components/sections/CarouselSection/utils/useVisibilityInterval';

import type { CarouselProps } from '../CarouselSection.types';

const slidesVariant = cva(
    'absolute left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%] overflow-hidden opacity-0 transition-[all] duration-300 ease-in-out',
    {
        variants: {
            state: {
                active: 'z-[2] opacity-100',
                prev: 'z-[1] translate-x-[-135%] translate-y-[-50%] scale-[0.85] cursor-pointer opacity-100',
                next: 'z-[1] translate-x-[35%] translate-y-[-50%] scale-[0.85] cursor-pointer opacity-100',
                default: 'z-[-1]',
            },
        },
    },
);

export default function Carousel({
    children,
    activeSlide,
    handleSlideChange,
}: CarouselProps): ReactNode {
    const [touchStart, setTouchStart] = useState<number>(0);

    const prev = activeSlide > 0 ? activeSlide - 1 : children.length - 1;
    const next = activeSlide < children.length - 1 ? activeSlide + 1 : 0;

    const slideTo = useCallback(
        (index: number): void => {
            handleSlideChange(index);
        },
        [handleSlideChange],
    );
    const slidePrev = useCallback((): void => slideTo(prev), [prev, slideTo]);
    const slideNext = useCallback((): void => slideTo(next), [next, slideTo]);

    const carouselRef = useVisibilityInterval(slideNext, 10000);

    const handleTouchStart = (event: React.TouchEvent) => {
        setTouchStart(event?.changedTouches[0]?.screenX || 0);
    };

    const handleTouchEnd = (event: React.TouchEvent) => {
        if (
            event?.changedTouches[0]?.screenX &&
            event.changedTouches[0].screenX > touchStart
        ) {
            slidePrev();
        } else {
            slideNext();
        }
    };

    const checkSlideState = (
        index: number,
    ): 'active' | 'prev' | 'next' | 'default' => {
        switch (true) {
            case index === activeSlide:
                return 'active';
            case index === prev:
                return 'prev';
            case index === next:
                return 'next';
            default:
                return 'default';
        }
    };

    const checkActiveDot = (index: number): boolean =>
        index - 1 === activeSlide ||
        (index === 0 && activeSlide === children.length - 1);

    const defineOnClickEvent = (slideIndex: number) => {
        if (slideIndex === prev) {
            return slidePrev;
        }
        if (slideIndex === next) {
            return slideNext;
        }
    };

    return (
        <>
            <div
                ref={carouselRef}
                className="relative h-[448px] w-full select-none md:h-[592px]"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {children.map((slide, index) => (
                    <div
                        key={'carousel-' + index}
                        className={cn(
                            slidesVariant({
                                state: checkSlideState(index),
                            }),
                        )}
                        onClick={defineOnClickEvent(index)}
                    >
                        {slide}
                    </div>
                ))}
            </div>

            <div className="mb-8 mt-4 flex w-full justify-center gap-2 md:my-8">
                {children.map((_, index) => (
                    <div
                        key={'carousel-dot-' + index}
                        onClick={() => {
                            slideTo(
                                index > 0 ? index - 1 : children.length - 1,
                            );
                        }}
                        className={cn(
                            'h-2 w-2 cursor-pointer rounded-full bg-grey-100',
                            checkActiveDot(index) && 'bg-green-600',
                        )}
                    />
                ))}
            </div>
        </>
    );
}
